/**
 * Purchase Order Configuration Component
 * Management of Purchase Order Configuration
 * Location: Settings → Order → Purchase Order Configuration
 */

import {
  Component,
  OnInit,
  signal,
  computed,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';

// PrimeNG services
import { MessageService } from 'primeng/api';

// Services and models
import { PurchaseOrderConfigService } from './services/purchase-order-config.service';
import {
  PurchaseOrderFieldItem,
  PURCHASE_ORDER_FIELD_OPTIONS,
} from './purchase-order-config.config';
import { PermissionService } from '@app/core/services/permission.service';

// Shared components
import { AppButtonComponent } from '@shared/components/app-button/app-button.component';

@Component({
  selector: 'app-purchase-order-config',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    ButtonModule,
    ToastModule,
    ToggleSwitchModule,
    AppButtonComponent,
  ],
  providers: [MessageService],
  templateUrl: './purchase-order-config.component.html',
  styleUrls: ['./purchase-order-config.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchaseOrderConfigComponent implements OnInit {
  private readonly messageService = inject(MessageService);
  protected readonly translateService = inject(TranslateService);
  private readonly purchaseOrderConfigService = inject(
    PurchaseOrderConfigService,
  );
  private readonly permissionService = inject(PermissionService);
  private readonly permissionCodes = toSignal(
    this.permissionService.userPermissions$,
    {
      initialValue: this.permissionService.getCurrentPermissions(),
    },
  );

  // Permission flags - computed from PermissionService
  // Using menu codes: settings.procurement.purchase_order_configuration.view/edit
  canRead = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.procurement.purchase_order_configuration.view');
  });
  canUpdate = computed(() => {
    this.permissionCodes();
    return this.permissionService.canDoAction('settings.procurement.purchase_order_configuration.edit');
  });

  // Signals for reactive state management
  selectedField = signal<string>('');
  loading = signal<boolean>(false);

  // Purchase order fields list for sidebar
  poFields = signal<PurchaseOrderFieldItem[]>([]);

  // Current field configuration
  currentFieldConfig = signal<PurchaseOrderFieldItem | null>(null);

  ngOnInit(): void {
    this.loadData();
  }

  /**
   * Load purchase order field configurations
   */
  loadData(): void {
    this.loading.set(true);
    this.purchaseOrderConfigService.getPurchaseOrderFields().subscribe({
      next: (fields) => {
        this.poFields.set(fields);
        // Select first field by default
        if (fields && fields.length > 0) {
          this.selectField(fields[0]);
        }
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading purchase order fields:', error);
        this.loading.set(false);
      },
    });
  }

  /**
   * Select a field from sidebar
   */
  selectField(field: PurchaseOrderFieldItem): void {
    this.selectedField.set(field.fieldKey);
    this.currentFieldConfig.set(field);
  }

  /**
   * Toggle configuration active status (enable/disable config)
   */
  toggleConfigActive(isActive: boolean): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const currentField = this.currentFieldConfig();
    if (!currentField) return;

    // Update the field in the list
    const updatedFields = this.poFields().map((field) =>
      field.fieldKey === currentField.fieldKey ? { ...field, isActive } : field,
    );
    this.poFields.set(updatedFields);

    // Update the current field configuration
    this.currentFieldConfig.set({
      ...currentField,
      isActive,
    });

    // Save to local storage
    this.purchaseOrderConfigService.savePurchaseOrderFieldsToStorage(
      updatedFields,
    );
  }

  /**
   * Toggle field requirement (required/optional)
   */
  toggleFieldRequirement(isRequired: boolean): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    const currentField = this.currentFieldConfig();
    if (!currentField) return;

    // Update the field in the list
    const updatedFields = this.poFields().map((field) =>
      field.fieldKey === currentField.fieldKey
        ? { ...field, isRequired }
        : field,
    );
    this.poFields.set(updatedFields);

    // Update the current field configuration
    this.currentFieldConfig.set({
      ...currentField,
      isRequired,
    });

    // Save to local storage
    this.purchaseOrderConfigService.savePurchaseOrderFieldsToStorage(
      updatedFields,
    );
  }

  /**
   * Get toggle switch style to override PrimeNG default colors
   */
  getToggleSwitchStyle() {
    return {
      '--p-toggleswitch-checked-background': '#3b82f6',
      '--p-toggleswitch-checked-border-color': '#3b82f6',
      '--p-toggleswitch-checked-hover-background': '#2563eb',
      '--p-toggleswitch-checked-hover-border-color': '#2563eb',
    };
  }

  /**
   * Get display name for field
   */
  getFieldDisplayName(fieldKey: string): string {
    const field = PURCHASE_ORDER_FIELD_OPTIONS.find(
      (option) => option.fieldKey === fieldKey,
    );
    return field ? this.translateService.instant(field.fieldName) : fieldKey;
  }

  /**
   * Handle form reset
   */
  onReset(): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.loading.set(true);
    this.purchaseOrderConfigService.resetPurchaseOrderFields().subscribe({
      next: (fields) => {
        this.poFields.set(fields);
        if (this.selectedField()) {
          const selected = fields.find(
            (f) => f.fieldKey === this.selectedField(),
          );
          if (selected) {
            this.currentFieldConfig.set(selected);
          }
        }
        this.loading.set(false);
        // Save to local storage
        this.purchaseOrderConfigService.savePurchaseOrderFieldsToStorage(
          fields,
        );
        this.messageService.add({
          severity: 'info',
          summary: this.translateService.instant('common.messages.info'),
          detail: this.translateService.instant(
            'common.messages.reset_success',
          ),
        });
      },
      error: (error) => {
        console.error('Error resetting purchase order fields:', error);
        this.loading.set(false);
      },
    });
  }

  /**
   * Handle form save
   */
  onSave(): void {
    if (!this.canUpdate()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('common.messages.warning'),
        detail: this.translateService.instant(
          'common.permissions.no_permission',
        ),
      });
      return;
    }
    this.loading.set(true);
    const fields = this.poFields();
    this.purchaseOrderConfigService
      .updatePurchaseOrderFields(fields)
      .subscribe({
        next: (updatedFields) => {
          this.poFields.set(updatedFields);
          // Update current field if selected
          if (this.selectedField()) {
            const selected = updatedFields.find(
              (f) => f.fieldKey === this.selectedField(),
            );
            if (selected) {
              this.currentFieldConfig.set(selected);
            }
          }
          this.loading.set(false);
          // Save to local storage
          this.purchaseOrderConfigService.savePurchaseOrderFieldsToStorage(
            updatedFields,
          );
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_order_config.messages.save_success',
            ),
          });
        },
        error: (error) => {
          console.error('Error saving purchase order fields:', error);
          this.loading.set(false);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_order_config.messages.save_error',
            ),
          });
        },
      });
  }
}
